import type { SearchFeedsResult, SearchMessageResult } from "@storyboard/web-client";
import { formatLocalDate } from "@/utils/dateFormat";

export interface PaginatedSearchData {
  results: SearchMessageResult[] | SearchFeedsResult[] | [];
  meta?: {
    query: string;
    limit: number;
    page: number;
    offset: number;
    size: number;
    loadMore: boolean;
  };
}

interface SearchResultsResponse {
  channels: PaginatedSearchData | null;
  messages: PaginatedSearchData | null;
}

const shouldLoadMore = ({ page, limit, size }: { page: number; limit: number; size: number }): boolean => {
  if (page === 0 && size < limit) {
    return false;
  }

  if (!size || size === 0) {
    return false;
  }
  return size >= limit;
};

const removeDeleted = (results: SearchMessageResult[]): SearchMessageResult[] => {
  return results?.filter((result) => result?.deletedAt === null || !result?.deletedAt);
};

/** Next offset for an infinite search query, or undefined once the last page came up short. */
export const nextSearchOffset = ({
  pages,
  limit,
}: {
  pages: { size?: number }[];
  limit: number;
}): number | undefined => {
  const lastPage = pages.at(-1);
  return shouldLoadMore({ page: pages.length - 1, limit, size: lastPage?.size ?? 0 })
    ? pages.length * limit
    : undefined;
};

/**
 * Flattens an infinite query's pages into the shape the results list renders: soft-deleted rows
 * filtered out (a lagging search index still returns them), plus the paging meta.
 */
export const searchPagesToPaginatedData = ({
  pages,
  query,
  limit,
}: {
  pages: { results?: SearchMessageResult[] | SearchFeedsResult[]; size?: number }[] | undefined;
  query: string;
  limit: number;
}): PaginatedSearchData => {
  if (!pages?.length) {
    return {} as PaginatedSearchData;
  }
  const page = pages.length - 1;
  const size = pages.at(-1)?.size ?? 0;
  return {
    results: removeDeleted(pages.flatMap((p) => (p?.results ?? []) as SearchMessageResult[])),
    meta: {
      query,
      limit,
      page,
      offset: pages.length * limit,
      size,
      loadMore: shouldLoadMore({ page, limit, size }),
    },
  };
};

export const getFormattedTime = (dateString: string) => {
  const date = new Date(dateString);
  // The meridiem in lowercase, as the search list has always shown it.
  return dateString ? `${formatLocalDate(date, "MM/dd/yy hh:mm")} ${formatLocalDate(date, "a").toLowerCase()}` : "";
};

export const prettyDate = (date: string) =>
  new Date(date)
    .toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
    .toString()
    .replace("at", " ");
